import { z } from 'zod';

import { resolveLivestreamEmbed } from '../lib/livestream-embed';

/**
 * An organizer-set apply link renders as a public `<a href>`, and
 * `z.string().url()` accepts `javascript:` / `data:`. This is the FOURTH path
 * into `auditions.external_link` — the organizer's own audition form — which
 * the import pipeline's `httpUrlSchema` never sees (2026-07-26 promote-gate
 * review, High 2). Same rule, enforced where this pipe starts.
 */
const httpUrl = z
  .string()
  .url()
  .refine(
    (value) => {
      try {
        const protocol = new URL(value).protocol;
        return protocol === 'http:' || protocol === 'https:';
      } catch {
        return false;
      }
    },
    { message: 'must be an http(s) URL' },
  );

// Status enums
export const AuditionStatusSchema = z.enum([
  'draft',
  'open',
  'closed',
  'archived',
]);
export const AuditionTypeSchema = z.enum(['private', 'public']);
export const ContractLengthSchema = z.enum(['life', 'seasonal', 'shortTerm']);
export const ApplicationStatusSchema = z.enum([
  'open',
  'issues',
  'submitted',
  'accepted',
  'completed',
  'rejected',
  'archived',
]);

// Location schema
export const AuditionLocationSchema = z.object({
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  postalCode: z.string().optional(),
  coordinates: z
    .object({
      lat: z.number(),
      lng: z.number(),
    })
    .optional(),
});

// Create audition schema
export const CreateAuditionSchema = z.object({
  title: z
    .string()
    .transform((v) => v.trim())
    .pipe(
      z
        .string()
        .min(1, 'Title is required')
        .max(50, 'Title must be at most 50 characters'),
    ),
  accountId: z.string().uuid('Invalid account ID'),
  // When true, create as a competition (is_competition=true) with the GDO
  // self-serve defaults: hide_apply_button + seeded application structure.
  // Record type is set at creation only — never flippable afterwards.
  isCompetition: z.boolean().default(false),
});

// Audition date schema
export const AuditionDateSchema = z.object({
  startDate: z.coerce.date(),
  endDate: z.coerce.date().optional(),
  startTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time format')
    .optional(),
  endTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time format')
    .optional(),
  ageMin: z.number().int().min(0).optional(),
  ageMax: z.number().int().min(0).optional(),
  location: AuditionLocationSchema.optional(),
});

// Update audition schema
export const UpdateAuditionSchema = z.object({
  // Basic Info
  title: z
    .string()
    .min(1)
    .max(50)
    .transform((v) => v.trim())
    .optional(),
  description: z.string().optional(),
  auditionType: AuditionTypeSchema.optional(),
  externalLink: httpUrl.optional().or(z.literal('')),
  rankIds: z.array(z.string().uuid()).optional(),

  // Requirements
  requirementGenders: z.array(z.string()).optional(),
  requirementAgeMin: z.number().int().min(0).optional().nullable(),
  requirementAgeMax: z.number().int().min(0).optional().nullable(),
  requirementHeightMaleMin: z.number().int().min(0).optional().nullable(),
  requirementHeightMaleMax: z.number().int().min(0).optional().nullable(),
  requirementHeightFemaleMin: z.number().int().min(0).optional().nullable(),
  requirementHeightFemaleMax: z.number().int().min(0).optional().nullable(),
  requirementOther: z.string().optional(),
  requirementMedia: z.array(z.string()).optional(),
  requirementDancePositions: z.array(z.string()).optional(),

  // Contract
  contractLength: ContractLengthSchema.optional().nullable(),
  contractStartDate: z.coerce.date().optional().nullable(),
  contractEndDate: z.coerce.date().optional().nullable(),

  // Application Settings
  openingDate: z.coerce.date().optional().nullable(),
  closingDate: z.coerce.date().optional().nullable(),
  autoClose: z.boolean().optional(),
  applyOnApp: z.boolean().optional(),
  byInvitationOnly: z.boolean().optional(),
  // Competition-only auto-accept toggle. When true, submitted registrations
  // skip organizer review and land on `accepted`; enabling it also
  // retroactively accepts the competition's existing `completed` rows (handled
  // in updateAuditionAction). Update-only — the DB default `false` covers
  // creation, so it is intentionally absent from CreateAuditionSchema.
  autoAcceptRegistrations: z.boolean().optional(),
  // When true (DB default), entrants may keep editing their registration after
  // acceptance (bounded by the open registration window); re-submitting follows
  // autoAcceptRegistrations. Applies to all auditions. Update-only — the DB
  // default `true` covers creation, so it is absent from CreateAuditionSchema.
  allowAcceptedEdits: z.boolean().optional(),
  // Shared access code that unlocks the Apply button for invitation-only
  // competitions. Edited on the Entry settings tab next to byInvitationOnly;
  // an empty string clears it (the mapper coerces '' → null).
  accessCode: z.string().max(50).optional().nullable(),
  videoPreSelection: z.boolean().optional(),
  applicationFeeAmount: z.number().min(0).optional().nullable(),
  applicationFeeCurrency: z.string().length(3).optional(),

  // Location
  useOrganizationLocation: z.boolean().optional(),
  datesUseSeparateLocations: z.boolean().optional(),

  // Additional
  additionalInformations: z.string().optional(),
  featured: z.boolean().optional(),

  // Competition Settings
  // NOTE: `isCompetition` is intentionally NOT updatable here. Record type is
  // set at creation only (`CreateAuditionSchema.isCompetition`) and is never
  // flippable afterwards (Locked Decision #7 / R7). The post-creation editor
  // exposes no toggle, and the update path must not carry it either — otherwise
  // a crafted request could flip a live audition into a competition (or back),
  // stranding seeded competition structure / partner portal state.
  //
  // `allowsMultiCategoryEntry` is also gone: under the split styles/category
  // model (PR #1171) one application holds every style the dancer competes in,
  // so a per-application multi-entry toggle is obsolete.
  hideApplyButton: z.boolean().optional(),
  isNetworkingEvent: z.boolean().optional(),
  hasFinalShow: z.boolean().optional(),
  // BAL-408: when false, hide the "My Meetings" tab in the competition
  // partner portal (and 404 the page). Defaults true on the row.
  showPartnerMeetings: z.boolean().optional(),
  hideIndividualParticipants: z.boolean().optional(),
  hideOrganizationParticipants: z.boolean().optional(),
  // US-128: admin gate revealing the participant list/profiles to PARTNER
  // organizations. Default false (hidden) on the row; enforced in RLS.
  participantsVisibleToPartners: z.boolean().optional(),
  networkingAllowOrgSelection: z.boolean().optional(),
  networkingAllowIndividualSelection: z.boolean().optional(),
  networkingOrgSelectionStart: z.coerce.date().optional().nullable(),
  networkingOrgSelectionEnd: z.coerce.date().optional().nullable(),
  networkingIndividualSelectionStart: z.coerce.date().optional().nullable(),
  networkingIndividualSelectionEnd: z.coerce.date().optional().nullable(),
  networkingShowMatchesToOrgs: z.boolean().optional(),
  networkingShowMatchesToIndividuals: z.boolean().optional(),
  competitionBranding: z
    .object({
      // Cover image is picked from the organization's media library; we store
      // the organization_media id and resolve it to a public URL at render.
      // (name/logo dropped: the public title is audition.title and the logo is
      // always the organization's logo — see the branding rework.)
      cover_media_id: z.string().uuid().optional().or(z.literal('')),
      accent_color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .optional()
        .or(z.literal('')),
      // http(s) only, like `externalLink` above. This renders as a public
      // `<a href>` on the audition page (public-audition-view-apply-rail.tsx,
      // public-audition-view.tsx) and is organizer-writable, so a bare
      // `z.string().url()` accepts `javascript:`/`data:` and stores an
      // executable href — the exact class this file's `httpUrl` was added to
      // close, missed 95 lines below it (2026-07-26 final gate, H-A). Its
      // siblings here were already hardened: `livestream_embed_url` has a host
      // allowlist and `support_button_destination` is mailto/https-only.
      website_url: httpUrl.optional().or(z.literal('')),
      // US-134 — livestream embed URL. Validated against the host allowlist
      // server-side (resolveLivestreamEmbed); a URL whose host is not an
      // approved provider, or that is not https, is rejected here so a
      // clickjacking / embed-abuse URL can never reach the iframe src.
      livestream_embed_url: z
        .string()
        .trim()
        .optional()
        .or(z.literal(''))
        .superRefine((val, ctx) => {
          if (!val) return; // empty clears the field
          if (resolveLivestreamEmbed(val) === null) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                'Enter an https URL from a supported provider (YouTube, Vimeo, Twitch, Dailymotion, or Facebook).',
            });
          }
        }),
      // US-135 — per-competition support button. Label falls back to the
      // default when empty; destination accepts a mailto:, wa.me/, or https
      // URL. An https destination opens in a new tab.
      support_button_label: z.string().max(40).optional().or(z.literal('')),
      support_button_destination: z
        .string()
        .trim()
        .optional()
        .or(z.literal(''))
        .superRefine((val, ctx) => {
          if (!val) return;
          const isMailto = /^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(val);
          const isHttps = /^https:\/\/\S+$/i.test(val);
          if (!isMailto && !isHttps) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                'Enter a mailto: address, a WhatsApp https://wa.me/ link, or an https URL.',
            });
          }
        }),
    })
    .optional()
    .nullable(),
});

// Publish audition schema
export const PublishAuditionSchema = z.object({
  auditionId: z.string().uuid('Invalid audition ID'),
});

// Application schema
//
// Styles/categories separation (2026-06-04): the dancer multi-selects
// style entries (competition_categories rows). Required when the
// competition defines style entries — enforced in the action, because
// plain auditions without entries legitimately submit none. The dancer's
// age division is server-computed, never client-supplied.
export const CreateApplicationSchema = z.object({
  auditionId: z.string().uuid('Invalid audition ID'),
  styleEntryIds: z
    .array(z.string().uuid('Invalid style entry ID'))
    .max(50, 'Too many style entries selected')
    .optional(),
  fields: z.record(z.unknown()).optional(),
  // Access code carried from the public unlock step. For an invitation-only
  // competition where the applicant has no invitation yet, the create action
  // redeems this code server-side (creating the invitation) so that unlocking
  // never required a dancer profile up front — the profile requirement lives
  // here, at registration, where it belongs.
  accessCode: z.string().min(1).max(50).optional(),
});

export const UpdateApplicationSchema = z.object({
  status: ApplicationStatusSchema.optional(),
  fields: z.record(z.unknown()).optional(),
  managementFields: z.record(z.unknown()).optional(),
});

// Filter schema
export const AuditionFilterSchema = z.object({
  status: z.array(AuditionStatusSchema).optional(),
  auditionType: z.array(AuditionTypeSchema).optional(),
  genders: z.array(z.string()).optional(),
  search: z.string().optional(),
  accountId: z.string().uuid().optional(),
  featured: z.boolean().optional(),
  // Record-type filter for the organizer list (true = competitions,
  // false = auditions, undefined = both).
  isCompetition: z.boolean().optional(),
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
});

// ==================== Application Form Builder Schemas ====================

// Field type enum
export const ApplicationFieldTypeSchema = z.enum([
  'text',
  'textarea',
  'number',
  'email',
  'phone',
  'date',
  'select',
  'multiselect',
  'checkbox',
  'checkboxGroup',
  'radio',
  'file',
  'video',
  'videoCategories',
  'experience',
  'competition_styles',
  'age_category',
  'institution_preferences',
  'url',
  'heading',
  'paragraph',
]);

// Field option schema
export const FieldOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
});

// Field validation schema
export const FieldValidationSchema = z.object({
  required: z.boolean().optional(),
  minLength: z.number().int().min(0).optional(),
  maxLength: z.number().int().min(1).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  pattern: z.string().optional(),
  patternMessage: z.string().optional(),
  fileTypes: z.array(z.string()).optional(),
  maxFileSize: z.number().int().min(0).optional(),
});

// Field condition schema
export const FieldConditionSchema = z.object({
  fieldKey: z.string().min(1),
  operator: z.enum([
    'equals',
    'notEquals',
    'contains',
    'notContains',
    'isEmpty',
    'isNotEmpty',
    'greaterThan',
    'lessThan',
    'greaterOrEqual',
    'lessOrEqual',
    'onOrBefore',
    'onOrAfter',
  ]),
  value: z
    .union([z.string(), z.number(), z.boolean(), z.array(z.string())])
    .optional(),
});

// Single field configuration schema
export const ApplicationFieldConfigSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(50)
    .regex(/^[a-z][a-zA-Z0-9_]*$/, 'Key must start with lowercase letter'),
  type: ApplicationFieldTypeSchema,
  label: z.string().min(1).max(100),
  placeholder: z.string().max(200).optional(),
  description: z.string().max(500).optional(),
  defaultValue: z.unknown().optional(),
  options: z.array(FieldOptionSchema).optional(),
  validation: FieldValidationSchema.optional(),
  conditions: z.array(FieldConditionSchema).optional(),
  conditionLogic: z.enum(['and', 'or']).optional(),
  width: z.enum(['full', 'half', 'third']).optional(),
  sortOrder: z.number().int().min(0),
  isManagementField: z.boolean().optional(),
  source: z.literal('profile').optional(),
  sourceKey: z.string().max(100).optional(),
  allowMultiple: z.boolean().optional(),
  maxFiles: z.number().int().min(1).max(50).optional(),
  locked: z.boolean().optional(),
  widget: z.literal('country').optional(),
});

// Step (group) schema — mirrors the Meteor application-process group shape.
export const ApplicationStepSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(50)
    .regex(/^[a-z][a-zA-Z0-9_]*$/, 'Step key must start with lowercase letter'),
  name: z.string().min(1).max(100),
  icon: z.string().max(50).optional(),
  fields: z.array(ApplicationFieldConfigSchema),
  displayConditions: z.array(FieldConditionSchema).optional(),
  conditionLogic: z.enum(['and', 'or']).optional(),
  sortOrder: z.number().int().min(0),
});

// Application structure schema. `groups` (step model) is optional and
// preferred when present; `fields` (legacy flat) is retained for back-compat.
export const ApplicationStructureSchema = z.object({
  version: z.number().int().min(1).default(1),
  fields: z.array(ApplicationFieldConfigSchema),
  groups: z.array(ApplicationStepSchema).optional(),
  settings: z
    .object({
      showProgressIndicator: z.boolean().optional(),
      allowSaveDraft: z.boolean().optional(),
      requireAllFields: z.boolean().optional(),
    })
    .optional(),
});

// Template ID schema
export const ApplicationTemplateIdSchema = z.enum([
  'basic',
  'dance_company',
  'workshop',
  'networking_event',
  'video_audition',
  'blank',
]);

// Update audition application structure schema
export const UpdateApplicationStructureSchema = z.object({
  auditionId: z.string().uuid('Invalid audition ID'),
  applicationStructure: ApplicationStructureSchema,
});

// Apply template schema
export const ApplyTemplateSchema = z.object({
  auditionId: z.string().uuid('Invalid audition ID'),
  templateId: ApplicationTemplateIdSchema,
});

// ==================== Networking/Selection Schemas ====================

// Selection item schema
export const NetworkingSelectionItemSchema = z.object({
  organizationId: z.string().uuid(),
  rank: z.number().int().min(1).max(10),
  notes: z.string().max(500).optional(),
});

// Submit selections schema
export const SubmitNetworkingSelectionsSchema = z.object({
  auditionId: z.string().uuid('Invalid audition ID'),
  selections: z
    .array(NetworkingSelectionItemSchema)
    .min(1)
    .max(10)
    .refine(
      (items) => {
        const ranks = items.map((i) => i.rank);
        return new Set(ranks).size === ranks.length;
      },
      { message: 'Each rank must be unique' },
    ),
});

// Export types
export type CreateAuditionInput = z.infer<typeof CreateAuditionSchema>;
export type UpdateAuditionInput = z.infer<typeof UpdateAuditionSchema>;
export type AuditionDateInput = z.infer<typeof AuditionDateSchema>;
export type CreateApplicationInput = z.infer<typeof CreateApplicationSchema>;
export type UpdateApplicationInput = z.infer<typeof UpdateApplicationSchema>;
export type AuditionFilterInput = z.infer<typeof AuditionFilterSchema>;
export type ApplicationFieldConfigInput = z.infer<
  typeof ApplicationFieldConfigSchema
>;
export type ApplicationStructureInput = z.infer<
  typeof ApplicationStructureSchema
>;
export type UpdateApplicationStructureInput = z.infer<
  typeof UpdateApplicationStructureSchema
>;
export type ApplyTemplateInput = z.infer<typeof ApplyTemplateSchema>;
export type SubmitNetworkingSelectionsInput = z.infer<
  typeof SubmitNetworkingSelectionsSchema
>;
